import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { musicThemeFor } from "../../audio/tracks";
import { backgroundFor } from "./backgrounds";
import { useGameAudio } from "../../audio/useGameAudio";
import { useGame } from "../../net/gameClient";
import type { DisplayCommand, PlayerLite, Snapshot } from "../../../shared/types";
import { Crescent, Confetti, FlourDust, Logo, OptGlyph, Spark, Steam } from "../../components/motifs";
import { Ico } from "../../components/shell";
import JoinQR from "../../components/JoinQR";
import { MoveArrow, OvenTimer, Stage } from "../../components/primitives";
import { navigate } from "../../router";
import { hostTokenFor } from "../../api";
import { mediaUrl } from "../../../shared/format";

export default function HostGame({ pin }: { pin: string }) {
  // Remote-control display: this tab is the clean big screen, driven by a phone.
  // The phone owns every control, so this surface hides them — unless the phone
  // drops for a while, when we reveal them as a fallback so play can continue.
  const displayMode = useMemo(
    () => new URLSearchParams(location.search).get("remote") === "1",
    [],
  );
  const [fsNudge, setFsNudge] = useState(false);

  // Fullscreen commands relayed from the phone. Exiting works without a gesture;
  // entering does not (browser policy) — so on a blocked enter we surface a nudge.
  const onCommand = useCallback((cmd: DisplayCommand) => {
    if (cmd === "exitFullscreen") {
      document.exitFullscreen?.().catch(() => {});
      setFsNudge(false);
    } else {
      document.documentElement
        .requestFullscreen?.()
        .then(() => setFsNudge(false))
        .catch(() => setFsNudge(true));
    }
  }, []);

  const game = useGame(
    pin,
    "host",
    hostTokenFor(pin),
    displayMode ? { surface: "display", onCommand } : undefined,
  );
  const snap = game.snapshot;
  const [exitOpen, setExitOpen] = useState(false);
  const live = !!snap && snap.phase !== "podium";

  const theme = useMemo(() => musicThemeFor(pin), [pin]);
  // Host-chosen big-screen background (picked at launch, remembered per PIN).
  const bg = useMemo(() => backgroundFor(pin).css, [pin]);
  const audio = useGameAudio({
    phase: snap?.phase,
    theme,
    mutedOverride: displayMode ? !!snap?.musicMuted : undefined,
  });

  // Phone-dropped fallback: if the controller is gone past a grace window, reveal
  // the big-screen controls so the host can finish from here. Hidden again the
  // moment the phone reconnects.
  const remoteGone = displayMode && snap?.remoteControl === true && snap?.remoteConnected === false;
  const [fallbackControls, setFallbackControls] = useState(false);
  useEffect(() => {
    if (!remoteGone) {
      setFallbackControls(false);
      return;
    }
    const id = setTimeout(() => setFallbackControls(true), 12000);
    return () => clearTimeout(id);
  }, [remoteGone]);
  // Controls are shown on the big screen normally, or in display mode only when
  // we've fallen back because the phone is gone.
  const controls = !displayMode || fallbackControls;

  // Native warning if the host closes/refreshes the tab mid-session.
  useEffect(() => {
    if (!live) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [live]);

  const onExit = () => setExitOpen(true);
  const exitModal = exitOpen ? (
    <HostExitModal
      onStay={() => setExitOpen(false)}
      onEnd={() => {
        setExitOpen(false);
        game.sendHost("end");
        navigate("/");
      }}
      onLeave={() => {
        setExitOpen(false);
        navigate("/");
      }}
    />
  ) : null;

  if (!snap) {
    return (
      <Stage bg={bg}>
        <div className="bk bk-grain" style={{ display: "grid", placeItems: "center" }}>
          <div style={{ textAlign: "center", position: "relative", zIndex: 1 }}>
            <Logo scale={1.2} />
            <p style={{ marginTop: 20, color: "var(--ink-soft)", fontWeight: 700 }}>
              {game.connected ? "Warming the oven…" : "Connecting…"}
            </p>
          </div>
        </div>
      </Stage>
    );
  }

  return (
    <Stage bg={bg}>
      {snap.phase === "lobby" && <HostLobby snap={snap} controls={controls} onStart={() => game.sendHost("start")} onExit={onExit} />}
      {(snap.phase === "question" || snap.phase === "reveal") && (
        <HostLive
          snap={snap}
          controls={controls}
          onReveal={() => game.sendHost("reveal")}
          onLeaderboard={() => game.sendHost("leaderboard")}
          onExit={onExit}
        />
      )}
      {snap.phase === "leaderboard" && <HostScoreboard snap={snap} controls={controls} onNext={() => game.sendHost("next")} onExit={onExit} />}
      {snap.phase === "podium" && <HostPodium snap={snap} />}
      {/* Big-screen music control — hidden in remote mode (the phone owns it). */}
      {theme.id !== "none" && !displayMode && <MuteButton audio={audio} />}
      {/* Remote mode: if autoplay was blocked there's no mute button to retry, so
          offer a tap-to-unblock chip a passer-by can hit. */}
      {displayMode && theme.id !== "none" && audio.blocked && <DisplaySoundChip onTap={() => audio.retry()} />}
      {/* The phone asked for fullscreen but the browser blocked the remote enter —
          a tap (a real gesture) here makes it go. */}
      {fsNudge && (
        <FullscreenNudge
          onTap={() =>
            document.documentElement
              .requestFullscreen?.()
              .then(() => setFsNudge(false))
              .catch(() => {})
          }
          onDismiss={() => setFsNudge(false)}
        />
      )}
      {exitModal}
    </Stage>
  );
}

/* A tap-to-unblock chip shown only when autoplay was blocked on the remote big
   screen (where there's no mute button to act as the unblock gesture). */
function DisplaySoundChip({ onTap }: { onTap: () => void }) {
  return (
    <button
      onClick={onTap}
      style={{
        position: "fixed", bottom: 56, left: 18, zIndex: 50, border: "none", borderRadius: 999,
        padding: "10px 16px", fontSize: 15, fontWeight: 800, cursor: "pointer", color: "#fff",
        background: "var(--red)", boxShadow: "0 6px 18px rgba(40,25,15,.28)",
      }}
    >
      🔊 Tap for sound
    </button>
  );
}

/* Full-screen tappable overlay: the phone requested fullscreen but the browser
   needs a gesture on this device, so we ask for one tap. */
function FullscreenNudge({ onTap, onDismiss }: { onTap: () => void; onDismiss: () => void }) {
  return (
    <div
      onClick={onTap}
      style={{
        position: "fixed", inset: 0, zIndex: 80, display: "grid", placeItems: "center",
        background: "rgba(40,25,15,.55)", cursor: "pointer", color: "#fff", textAlign: "center",
      }}
    >
      <div>
        <div style={{ fontSize: 22, fontWeight: 800 }}>Tap to go full screen</div>
        <div style={{ marginTop: 8, fontWeight: 600, opacity: 0.85 }}>Requested from your phone</div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          className="bk-btn bk-btn--ghost"
          style={{ marginTop: 18, background: "rgba(255,255,255,.16)", color: "#fff" }}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

/** Floating music control on the host screen. Doubles as the autoplay-unblock
    gesture: when the browser blocked autoplay, the first tap starts playback. */
function MuteButton({ audio }: { audio: ReturnType<typeof useGameAudio> }) {
  const label = audio.blocked ? "🔊 Tap for sound" : audio.muted ? "🔇" : "🔊";
  return (
    <button
      onClick={() => (audio.blocked ? audio.retry() : audio.toggleMute())}
      title={audio.muted ? "Unmute music" : "Mute music"}
      style={{
        position: "fixed",
        bottom: 56,
        left: 18,
        zIndex: 50,
        border: "none",
        borderRadius: 999,
        padding: audio.blocked ? "10px 16px" : "10px 14px",
        fontSize: 15,
        fontWeight: 800,
        cursor: "pointer",
        color: "#fff",
        background: audio.blocked ? "var(--red)" : "rgba(40,25,15,.55)",
        boxShadow: "0 6px 18px rgba(40,25,15,.28)",
        backdropFilter: "blur(4px)",
      }}
    >
      {label}
    </button>
  );
}

/* ───────────────────────── LOBBY ─────────────────────────
   One continuous joining screen. It opens as a big centred hero (large PIN +
   QR, the "welcome" look) and, as bakers arrive, the PIN + QR glide up into a
   calm top band while the roster fills the room below. No layout switch — a
   single DOM tree whose pieces reflow, with the PIN/QR animated via FLIP. The
   reflow is driven purely by the crowd (REFLOW_AT), never by a timer. */
const REFLOW_AT = 6;

/* FLIP: glide `el` from where it was (`first`) to where it sits now, so the
   PIN and QR travel smoothly when the layout reflows instead of jumping. */
function flip(el: HTMLElement | null, first?: DOMRect) {
  if (!el || !first) return;
  const last = el.getBoundingClientRect();
  if (!last.width || !first.width) return;
  const dx = first.left - last.left;
  const dy = first.top - last.top;
  const s = first.width / last.width;
  if (Math.abs(dx) < 1 && Math.abs(dy) < 1 && Math.abs(s - 1) < 0.01) return;
  el.style.transformOrigin = "top left";
  el.style.transition = "none";
  el.style.transform = `translate(${dx}px, ${dy}px) scale(${s})`;
  requestAnimationFrame(() => {
    el.style.transition = "transform .55s cubic-bezier(.22,1,.36,1)";
    el.style.transform = "";
  });
}

function HostLobby({ snap, controls, onStart, onExit }: { snap: Snapshot; controls: boolean; onStart: () => void; onExit: () => void }) {
  const teamsMode = snap.mode === "teams";
  // After 30s the screen settles into the dense band even if the room is still
  // quiet, so a lingering lobby doesn't sit on the giant hero PIN forever. It
  // glides into the SAME band view (not a separate layout) via the reflow.
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setTimedOut(true), 30000);
    return () => clearTimeout(id);
  }, []);
  // Teams open as a fixed roster, so they start straight in the dense band.
  const dense = teamsMode || timedOut || snap.playerCount >= REFLOW_AT;
  const teamCount = teamsMode ? snap.players.length : snap.playerCount;

  // Glide the PIN + QR between the hero and the band whenever density flips.
  const pinRef = useRef<HTMLDivElement>(null);
  const qrRef = useRef<HTMLDivElement>(null);
  const prevRects = useRef<{ pin?: DOMRect; qr?: DOMRect }>({});
  useLayoutEffect(() => {
    flip(pinRef.current, prevRects.current.pin);
    flip(qrRef.current, prevRects.current.qr);
    prevRects.current = {
      pin: pinRef.current?.getBoundingClientRect(),
      qr: qrRef.current?.getBoundingClientRect(),
    };
  }, [dense]);

  const TeamChip = ({ p }: { p: PlayerLite }) => (
    <span
      className="bk-chip"
      style={{
        fontSize: 14,
        padding: dense ? "8px 13px" : "9px 16px",
        width: dense && !teamsMode ? "100%" : undefined,
        justifyContent: "flex-start",
        animation: "bk-rise .4s ease both",
      }}
    >
      <span
        style={{
          width: 22,
          height: 22,
          borderRadius: 7,
          background: p.color,
          color: "#fff",
          display: "grid",
          placeItems: "center",
          fontWeight: 800,
          fontSize: teamsMode ? 13 : 11,
          flex: "0 0 auto",
        }}
      >
        {p.emoji || p.init}
      </span>
      <span style={{ color: "var(--ink)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {p.nickname}
      </span>
      {teamsMode && (
        <span className="mono" style={{ marginLeft: "auto", color: "var(--ink-faint)", fontWeight: 700 }}>
          {p.memberCount ?? 0}
        </span>
      )}
    </span>
  );

  // A team card with its member initials — used in the dense roster for teams mode.
  const TeamCard = ({ p }: { p: PlayerLite }) => (
    <div
      className="bk-card"
      style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 9, animation: "bk-rise .4s ease both" }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
        <span style={{ fontSize: 22 }}>{p.emoji}</span>
        <span style={{ fontWeight: 800, fontSize: 15, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {p.nickname}
        </span>
        <span className="mono" style={{ marginLeft: "auto", fontWeight: 700, color: "var(--red)" }}>
          {p.memberCount ?? 0}
        </span>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 5, minHeight: 22 }}>
        {(p.members ?? []).map((m) => (
          <span
            key={m.nickname}
            title={m.nickname}
            style={{
              width: 22,
              height: 22,
              borderRadius: 7,
              background: p.color,
              color: "#fff",
              display: "grid",
              placeItems: "center",
              fontWeight: 800,
              fontSize: 9.5,
            }}
          >
            {m.init}
          </span>
        ))}
        {(p.memberCount ?? 0) === 0 && (
          <span style={{ fontSize: 12, fontWeight: 700, color: "var(--ink-faint)" }}>waiting…</span>
        )}
      </div>
    </div>
  );

  const startBtn = (
    <button
      className="bk-btn bk-btn--primary bk-btn--xl"
      onClick={onStart}
      disabled={snap.playerCount === 0}
    >
      <Ico name="play" size={18} /> Start the bake
    </button>
  );

  const players = snap.players;

  return (
    <div
      className="bk bk-grain"
      style={{
        position: "relative",
        height: "100%",
        minHeight: "100vh",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        justifyContent: dense ? "flex-start" : "center",
      }}
    >
      {/* fixed chrome — logo + quiz title (top-left), controls (top-right) */}
      <div style={{ position: "absolute", top: 24, left: 32, zIndex: 4, display: "flex", flexDirection: "column", gap: 8 }}>
        <Logo />
        <div className="bk-eyebrow bk-eyebrow--red">{snap.quizTitle.toUpperCase()}</div>
      </div>
      {/* In the hero the QR is centred, so the controls live in the top-right
          corner. In the dense band the QR moves to that corner, so the controls
          relocate to the roster header row below (see ROSTER ZONE). */}
      {!dense && (
        <div style={{ position: "absolute", top: 24, right: 32, zIndex: 4, display: "flex", alignItems: "center", gap: 10 }}>
          <span className="bk-chip" style={{ fontSize: 14, padding: "8px 16px" }}>
            <span className="bk-dot" style={{ background: "var(--good)" }} /> {snap.playerCount} baking
          </span>
          {controls && <FullscreenButton />}
          {controls && <EndButton onExit={onExit} />}
        </div>
      )}

      {/* HEADER ZONE — hero ⇄ band. The PIN + QR glide between the two via FLIP. */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          flex: "0 0 auto",
          display: "flex",
          flexDirection: dense ? "row" : "column",
          alignItems: "center",
          justifyContent: "center",
          gap: dense ? 24 : 10,
          padding: dense ? "26px 40px 20px" : "0 44px",
          borderBottom: dense ? "1px solid var(--line-soft)" : "1px solid transparent",
          transition: "padding .45s ease, border-color .45s ease",
        }}
      >
        {/* balances the right-hand QR so the PIN stays optically centred in the band */}
        <div aria-hidden style={{ width: dense ? 174 : 0, flex: "0 0 auto" }} />

        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: dense ? "1 1 auto" : "0 0 auto", minWidth: 0 }}>
          {snap.quizEmoji && (
            <div
              aria-hidden
              style={{
                width: dense ? 0 : 104,
                height: dense ? 0 : 104,
                opacity: dense ? 0 : 1,
                borderRadius: 24,
                background: "var(--surface)",
                boxShadow: dense ? "none" : "var(--sh-lg)",
                display: "grid",
                placeItems: "center",
                fontSize: dense ? 0 : 60,
                lineHeight: 1,
                overflow: "hidden",
                marginBottom: dense ? 0 : 14,
                transition: "width .4s ease, height .4s ease, opacity .35s ease, font-size .4s ease, margin .4s ease",
              }}
            >
              {snap.quizEmoji}
            </div>
          )}
          <h1
            style={{
              fontSize: 34,
              fontWeight: 800,
              letterSpacing: "-.03em",
              textAlign: "center",
              opacity: dense ? 0 : 1,
              maxHeight: dense ? 0 : 60,
              width: dense ? 0 : "auto",
              overflow: "hidden",
              margin: dense ? 0 : "0 0 6px",
              transition: "opacity .3s ease, max-height .45s ease, margin .45s ease",
            }}
          >
            Join at the game PIN
          </h1>
          <div
            className="bk-eyebrow"
            style={{
              opacity: dense ? 1 : 0,
              maxHeight: dense ? 18 : 0,
              overflow: "hidden",
              marginBottom: dense ? 4 : 0,
              transition: "opacity .3s ease .1s, max-height .3s ease",
            }}
          >
            JOIN AT THE GAME PIN
          </div>
          <div
            ref={pinRef}
            className="mono"
            style={{
              fontSize: dense ? 46 : 88,
              fontWeight: 700,
              letterSpacing: dense ? ".06em" : ".12em",
              color: "var(--red)",
              lineHeight: 1.05,
              transition: "font-size .55s cubic-bezier(.22,1,.36,1), letter-spacing .55s ease",
            }}
          >
            {formatPin(snap.pin)}
          </div>
        </div>

        <div ref={qrRef} style={{ flex: "0 0 auto" }}>
          <JoinQR pin={snap.pin} size={dense ? 150 : 188} caption={dense ? "SCAN TO JOIN" : "SCAN TO JOIN — OR ENTER THE PIN"} />
        </div>
      </div>

      {/* the /play hint — hero only */}
      {!dense && (
        <p style={{ position: "relative", zIndex: 1, textAlign: "center", color: "var(--ink-soft)", fontWeight: 600, fontSize: 16, margin: "16px 0 0" }}>
          Open <b>/play</b> on your phone and pop in the PIN. 🥐
        </p>
      )}

      {/* ROSTER ZONE — bakers flow in here and fill the room as it gets busy */}
      <div
        className="bk-scroll"
        style={{
          position: "relative",
          zIndex: 1,
          flex: dense ? "1 1 auto" : "0 0 auto",
          minHeight: 0,
          overflowY: dense ? "auto" : "visible",
          padding: dense ? "16px 40px 104px" : "20px 44px 0",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {dense && (
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
            <h2 style={{ fontSize: 24, fontWeight: 800, letterSpacing: "-.025em" }}>
              {teamsMode ? "Teams forming" : "Bakers in the kitchen"}
            </h2>
            <span className="mono" style={{ fontSize: 20, fontWeight: 700, color: "var(--red)" }}>{teamCount}</span>
            <span className="bk-chip bk-chip--sage" style={{ marginLeft: "auto", fontSize: 13 }}>
              <span className="bk-dot" style={{ background: "var(--good)" }} /> still rising
            </span>
            {controls && <FullscreenButton />}
            {controls && <EndButton onExit={onExit} />}
          </div>
        )}

        <div
          style={
            dense
              ? {
                  display: "grid",
                  gridTemplateColumns: teamsMode
                    ? "repeat(auto-fill, minmax(230px, 1fr))"
                    : "repeat(auto-fill, minmax(180px, 1fr))",
                  gap: teamsMode ? 12 : 9,
                  alignContent: "start",
                }
              : { display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center", maxWidth: 920, margin: "0 auto" }
          }
        >
          {teamsMode
            ? players.map((p) => <TeamCard key={p.nickname} p={p} />)
            : players.map((p) => <TeamChip key={p.nickname} p={p} />)}
          {players.length === 0 && (
            <span style={{ color: "var(--ink-faint)", fontWeight: 700 }}>Waiting for the first baker to rise…</span>
          )}
        </div>
      </div>

      {/* Start — parked bottom-right (hidden on the clean remote big screen). */}
      {controls && <div style={{ position: "absolute", right: 32, bottom: 28, zIndex: 5 }}>{startBtn}</div>}
    </div>
  );
}

/* ───────────────────────── LIVE QUESTION / REVEAL ───────────────────────── */
function HostLive({
  snap,
  controls,
  onReveal,
  onLeaderboard,
  onExit,
}: {
  snap: Snapshot;
  controls: boolean;
  onReveal: () => void;
  onLeaderboard: () => void;
  onExit: () => void;
}) {
  const q = snap.question!;
  const revealing = snap.phase === "reveal";
  const teamsMode = snap.mode === "teams";
  const unit = snap.playerCount === 1 ? "baker" : "bakers";
  const correctIds = new Set(snap.reveal?.correctIds ?? []);
  const denom = Math.max(1, snap.playerCount);

  return (
    <div className="bk bk-grain" style={{ padding: "26px 40px 28px", display: "flex", flexDirection: "column" }}>
      {/* Logo gets its own centred line so it reads from across the room, then a
          second row carries the meta chips (left) and live controls (right). */}
      <div style={{ display: "flex", justifyContent: "center", position: "relative", zIndex: 1 }}>
        <Logo scale={2.1} />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 18, position: "relative", zIndex: 1, marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--line-soft)" }}>
        <span className="bk-chip bk-chip--red" style={{ fontSize: 14, padding: "8px 16px" }}>
          <span className="mono" style={{ fontWeight: 700 }}>Q{snap.index + 1}</span> / {snap.total}
        </span>
        {q.category && (
          <span className="bk-chip bk-chip--gold" style={{ fontSize: 14, padding: "8px 16px" }}>
            {q.category}
          </span>
        )}
        <div style={{ flex: 1 }} />
        <span className="bk-chip" style={{ fontSize: 14, padding: "8px 16px" }}>
          <span className="bk-dot" style={{ background: "var(--good)" }} /> {snap.playerCount} {teamsMode ? (snap.playerCount === 1 ? "baker" : "bakers") : "baking"}
        </span>
        {/* The projected display can't be clicked, so it keeps a small inline QR
            for latecomers; the host's screen tucks it into the ⋯ menu instead. */}
        {!controls && <JoinQR pin={snap.pin} size={72} />}
        {revealing ? (
          <span className="bk-chip bk-chip--sage" style={{ fontSize: 14, padding: "10px 16px" }}>
            <Ico name="check" size={16} /> {snap.reveal?.correctPct ?? 0}% correct
          </span>
        ) : (
          <OvenTimer size={92} stroke={8} endsAt={q.endsAt} timeLimit={q.timeLimit} />
        )}
        {controls && <HostMenu pin={snap.pin} onExit={onExit} />}
      </div>

      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          gap: 14,
          position: "relative",
          zIndex: 1,
          padding: "8px 40px",
        }}
      >
        {q.eyebrow && <div className="bk-eyebrow bk-eyebrow--red">{q.eyebrow}</div>}
        <h1 style={{ fontSize: 46, fontWeight: 800, letterSpacing: "-.03em", lineHeight: 1.08, maxWidth: 920 }}>
          {q.prompt}
        </h1>
        {q.imageKey && (
          <img
            src={mediaUrl(q.imageKey)}
            alt=""
            style={{ maxHeight: 240, maxWidth: "70%", objectFit: "contain", borderRadius: 18, boxShadow: "var(--sh)", marginTop: 4 }}
          />
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, position: "relative", zIndex: 1 }}>
        {q.options.map((o) => {
          const count = snap.reveal?.distribution[o.id] ?? 0;
          const pct = Math.round((count / denom) * 100);
          const isCorrect = correctIds.has(o.id);
          const dim = revealing && !isCorrect;
          return (
            <div
              key={o.id}
              className={`bk-opt bk-opt--${o.key}${dim ? " bk-opt--dim" : ""}${revealing && isCorrect ? " bk-opt--win" : ""}`}
              style={{ fontSize: 22, padding: "20px 24px" }}
            >
              <span className="bk-opt__glyph">
                <OptGlyph kind={o.key} size={26} />
              </span>
              {o.imageKey && (
                <img src={mediaUrl(o.imageKey)} alt="" style={{ height: 46, width: 46, objectFit: "cover", borderRadius: 10 }} />
              )}
              <span style={{ flex: 1 }}>{o.text}</span>
              {revealing && (
                <span style={{ display: "flex", alignItems: "center", gap: 14, flex: "0 0 auto" }}>
                  <span style={{ textAlign: "right", lineHeight: 1.05 }}>
                    <span className="mono" style={{ fontSize: 27, fontWeight: 800, display: "block" }}>{pct}%</span>
                    <span style={{ fontSize: 12.5, fontWeight: 700, opacity: 0.8 }}>
                      {count} {count === 1 ? "baker" : "bakers"}
                    </span>
                  </span>
                  {isCorrect ? (
                    <span style={{ width: 40, height: 40, borderRadius: "50%", background: "#fff", color: "var(--good)", display: "grid", placeItems: "center", boxShadow: "var(--sh-sm)" }}>
                      <Ico name="check" size={24} />
                    </span>
                  ) : (
                    <span style={{ width: 40, height: 40, borderRadius: "50%", background: "rgba(0,0,0,.16)", color: "#fff", display: "grid", placeItems: "center", fontSize: 19, fontWeight: 800, lineHeight: 1 }}>
                      ✕
                    </span>
                  )}
                </span>
              )}
              {revealing && (
                <span
                  style={{
                    position: "absolute",
                    left: 0,
                    bottom: 0,
                    height: 7,
                    width: `${pct}%`,
                    background: "rgba(255,255,255,.6)",
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 18, position: "relative", zIndex: 1 }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: "var(--ink-soft)" }}>
          <span className="mono" style={{ color: "var(--red)" }}>
            {q.answeredCount}
          </span>{" "}
          of {snap.playerCount} {unit} have answered
        </span>
        <div style={{ flex: 1 }} />
        {controls &&
          (revealing ? (
            <button className="bk-btn bk-btn--primary bk-btn--lg" onClick={onLeaderboard}>
              Show leaderboard →
            </button>
          ) : (
            <button className="bk-btn bk-btn--ghost bk-btn--lg" onClick={onReveal}>
              <Ico name="check" size={17} /> Reveal answer
            </button>
          ))}
      </div>
    </div>
  );
}

/* ───────────────────────── SCOREBOARD ───────────────────────── */
function HostScoreboard({ snap, controls, onNext, onExit }: { snap: Snapshot; controls: boolean; onNext: () => void; onExit: () => void }) {
  const last = snap.index + 1 >= snap.total;
  const teamsMode = snap.mode === "teams";
  // Whoever is on the longest current run → the "hottest streak" callout.
  const hot = snap.players.reduce<PlayerLite | null>(
    (best, p) => (p.streak > (best?.streak ?? 0) ? p : best),
    null,
  );
  const showStreak = !!hot && hot.streak >= 2;
  // Cap the content to a centred column so names and right-aligned scores stay a
  // sensible distance apart on a big projected screen (rather than full-width).
  // Match HostLive's outer padding so the foot action button lands in the exact
  // same spot — lets the host click reveal → leaderboard → next without moving
  // the cursor. Only the ranked rows are width-capped (COL); the header/foot
  // span full width so their corners line up with the other screens.
  const COL = 1000;
  return (
    <div className="bk bk-grain" style={{ padding: "26px 40px 28px", display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "flex-end", position: "relative", zIndex: 1 }}>
        <div>
          <div className="bk-eyebrow bk-eyebrow--red" style={{ marginBottom: 6 }}>
            AFTER QUESTION {snap.index + 1} OF {snap.total}
          </div>
          <h1 style={{ fontSize: 40, fontWeight: 800, letterSpacing: "-.03em" }}>Scoreboard</h1>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 14 }}>
          <span className="bk-chip" style={{ fontSize: 14, padding: "8px 16px", fontWeight: 700 }}>
            PIN {formatPin(snap.pin)}
          </span>
          <JoinQR pin={snap.pin} size={60} />
          {controls && <FullscreenButton />}
          {controls && <EndButton onExit={onExit} />}
        </div>
      </div>

      <div style={{ width: "100%", maxWidth: COL, margin: "0 auto", display: "flex", flexDirection: "column", gap: 10, flex: 1, justifyContent: "center", position: "relative", zIndex: 1 }}>
        {snap.players.slice(0, 5).map((t, i) => (
          <div
            key={t.nickname}
            className="bk-card"
            style={{
              padding: teamsMode ? "10px 18px 12px" : 0,
              animation: "bk-rise .5s cubic-bezier(.2,.8,.2,1) both",
              animationDelay: `${i * 0.07}s`,
              boxShadow: i === 0 ? "0 0 0 2px var(--gold), var(--sh)" : "var(--sh-sm)",
            }}
          >
            <div
              className="bk-row"
              style={{ padding: teamsMode ? "2px 0" : "13px 20px", gap: 16, boxShadow: "none", background: "transparent", border: "none" }}
            >
              <div className={`bk-rank bk-rank--${i < 3 ? i + 1 : ""}`}>{i + 1}</div>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 13,
                  background: t.color,
                  color: "#fff",
                  display: "grid",
                  placeItems: "center",
                  fontWeight: 800,
                  fontSize: teamsMode ? 20 : 15,
                  flex: "0 0 auto",
                }}
              >
                {t.emoji || t.init}
              </div>
              <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 11 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 800, fontSize: 18, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {t.nickname}
                  </div>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--ink-faint)" }}>
                    {teamsMode ? `${t.memberCount ?? 0} ${(t.memberCount ?? 0) === 1 ? "baker" : "bakers"} · ${t.tag}` : t.tag}
                  </div>
                </div>
                {t.streak >= 2 && (
                  <span className="bk-chip bk-chip--red" style={{ fontSize: 12.5, padding: "3px 9px", gap: 5, fontWeight: 800, flex: "0 0 auto" }}>
                    <Ico name="fire" size={13} /> {t.streak}
                  </span>
                )}
              </div>
              <div style={{ flex: 1 }} />
              <MoveArrow dir={t.move} />
              <div className="mono tnum" style={{ fontWeight: 700, fontSize: 22, width: 96, textAlign: "right" }}>
                {t.score.toLocaleString()}
              </div>
            </div>
            {teamsMode && (t.members?.length ?? 0) > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 7, paddingLeft: 56, marginTop: 6 }}>
                {t.members!.map((m) => (
                  <span
                    key={m.nickname}
                    className="bk-chip"
                    style={{ fontSize: 11.5, padding: "4px 9px", gap: 6, opacity: m.answered ? 1 : 0.6 }}
                  >
                    <span
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: 5,
                        background: t.color,
                        color: "#fff",
                        display: "grid",
                        placeItems: "center",
                        fontWeight: 800,
                        fontSize: 8,
                      }}
                    >
                      {m.init}
                    </span>
                    {m.nickname}
                    <span className="mono" style={{ color: "var(--ink-faint)", fontWeight: 700 }}>
                      {m.score.toLocaleString()}
                    </span>
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
        {snap.players.length === 0 && (
          <div style={{ color: "var(--ink-faint)", fontWeight: 700 }}>No scores yet.</div>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 18, position: "relative", zIndex: 1 }}>
        {showStreak ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 11,
              background: "linear-gradient(100deg, var(--gold), var(--caramel))",
              color: "#fff",
              borderRadius: 14,
              padding: "11px 18px",
              fontWeight: 800,
              fontSize: 16,
              boxShadow: "var(--sh)",
            }}
          >
            <span style={{ width: 30, height: 30, borderRadius: "50%", background: "rgba(255,255,255,.25)", display: "grid", placeItems: "center" }}>
              <Ico name="fire" size={16} />
            </span>
            {hot!.nickname} is on the hottest streak — {hot!.streak} in a row!
          </div>
        ) : (
          <span style={{ fontSize: 14, color: "var(--ink-soft)", fontWeight: 700 }}>
            {Math.max(0, snap.total - snap.index - 1)} questions left · anything can still rise
          </span>
        )}
        {controls && (
          <button className="bk-btn bk-btn--primary bk-btn--lg" style={{ marginLeft: "auto" }} onClick={onNext}>
            {last ? "Final results →" : "Next question →"}
          </button>
        )}
      </div>
    </div>
  );
}

/* ───────────────────────── PODIUM ───────────────────────── */
function HostPodium({ snap }: { snap: Snapshot }) {
  const p = snap.players;
  const teamsMode = snap.mode === "teams";
  const order: { player: PlayerLite; place: number; h: number }[] = [];
  if (p[1]) order.push({ player: p[1], place: 2, h: 168 });
  if (p[0]) order.push({ player: p[0], place: 1, h: 226 });
  if (p[2]) order.push({ player: p[2], place: 3, h: 128 });

  return (
    <div
      className="bk"
      style={{ overflow: "hidden", background: "radial-gradient(120% 90% at 50% -10%, #FFF3DC 0%, var(--cream) 55%)" }}
    >
      <Confetti count={46} />
      <FlourDust count={14} />
      <div style={{ position: "absolute", top: 16, right: 16, zIndex: 3 }}>
        <FullscreenButton />
      </div>
      <div
        style={{
          position: "relative",
          zIndex: 2,
          height: "100%",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "34px 40px 28px",
        }}
      >
        <div className="bk-eyebrow bk-eyebrow--red" style={{ letterSpacing: ".22em" }}>
          ★ BAKEOFF · {snap.quizTitle.toUpperCase()}
        </div>
        <h1 style={{ fontSize: 44, fontWeight: 800, letterSpacing: "-.03em", marginTop: 8, textAlign: "center" }}>
          We have a <span style={{ color: "var(--red)" }}>BakeOff Champion!</span>
        </h1>

        <div
          style={{ display: "flex", alignItems: "flex-end", justifyContent: "center", gap: 22, marginTop: "auto", width: "100%", maxWidth: 760 }}
        >
          {order.map(({ player: t, place, h }) => (
            <div key={t.nickname} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
              {place === 1 && (
                <div style={{ marginBottom: -2, animation: "bk-float 3s ease-in-out infinite" }}>
                  <Spark size={30} color="var(--gold)" />
                </div>
              )}
              <div
                style={{
                  width: place === 1 ? 86 : 66,
                  height: place === 1 ? 86 : 66,
                  borderRadius: 24,
                  background: t.color,
                  color: "#fff",
                  display: "grid",
                  placeItems: "center",
                  fontWeight: 800,
                  fontSize: place === 1 ? 30 : 22,
                  boxShadow: "var(--sh-lg)",
                  border: place === 1 ? "3px solid var(--gold)" : "none",
                }}
              >
                {t.emoji || t.init}
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontWeight: 800, fontSize: place === 1 ? 20 : 16, letterSpacing: "-.02em" }}>{t.nickname}</div>
                <div className="mono" style={{ fontSize: 14, fontWeight: 700, color: "var(--red)" }}>
                  {t.score.toLocaleString()}
                </div>
                {teamsMode && (
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--ink-faint)" }}>
                    {t.memberCount ?? 0} {(t.memberCount ?? 0) === 1 ? "baker" : "bakers"}
                  </div>
                )}
              </div>
              <div
                style={{
                  width: "100%",
                  height: h,
                  borderRadius: "16px 16px 0 0",
                  background:
                    place === 1
                      ? "linear-gradient(var(--gold), var(--gold-deep))"
                      : "linear-gradient(var(--surface), var(--surface-warm))",
                  border: "1px solid var(--line)",
                  borderBottom: "none",
                  boxShadow: "var(--sh)",
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "center",
                  paddingTop: 16,
                  position: "relative",
                }}
              >
                <div className="mono" style={{ fontSize: place === 1 ? 52 : 40, fontWeight: 700, color: place === 1 ? "#fff" : "var(--crust-soft)" }}>
                  {place}
                </div>
                {place === 1 && (
                  <div style={{ position: "absolute", bottom: 14, left: "50%", transform: "translateX(-50%)" }}>
                    <span className="bk-chip" style={{ background: "rgba(255,255,255,.9)", color: "var(--red)", fontWeight: 800, border: "none" }}>
                      CHAMPION
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
          {p.length === 0 && <div style={{ color: "var(--ink-soft)", fontWeight: 700 }}>No teams played.</div>}
        </div>

        <button
          className="bk-btn bk-btn--ghost bk-btn--lg"
          style={{ marginTop: 26 }}
          onClick={() => navigate("/results")}
        >
          <Ico name="chart" size={16} /> View results & insights
        </button>
      </div>
    </div>
  );
}

function formatPin(pin: string): string {
  return pin.length === 6 ? `${pin.slice(0, 3)} ${pin.slice(3)}` : pin;
}

/* Toggle the browser into full screen — handy when projecting the lobby on a
   TV. Stays full screen through the rest of the game (same page). Hidden if
   the browser doesn't support the Fullscreen API. */
/* Enter full screen. Available on every host phase; hides itself once full
   screen (Esc exits) so the projected screen stays clean. */
function FullscreenButton() {
  const fs = useIsFullscreen();
  if (typeof document === "undefined" || !document.documentElement.requestFullscreen) return null;
  if (fs) return null;
  return (
    <button
      className="bk-btn bk-btn--ghost"
      onClick={() =>
        document.documentElement.requestFullscreen?.().catch(() => {
          /* user denied or unavailable — ignore */
        })
      }
      title="Full screen"
      aria-label="Full screen"
      style={{ padding: "9px 15px", fontSize: 13.5 }}
    >
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 9V4h5" /><path d="M20 9V4h-5" /><path d="M4 15v5h5" /><path d="M20 15v5h-5" />
      </svg>
      Full screen
    </button>
  );
}

function useIsFullscreen(): boolean {
  const [fs, setFs] = useState(false);
  useEffect(() => {
    const sync = () => setFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", sync);
    return () => document.removeEventListener("fullscreenchange", sync);
  }, []);
  return fs;
}

/* Close / end the session. Hidden in full screen to keep the projected view clean. */
function EndButton({ onExit, label = "Close" }: { onExit: () => void; label?: string }) {
  if (useIsFullscreen()) return null;
  return (
    <button
      className="bk-btn bk-btn--ghost"
      onClick={onExit}
      title="End or leave this session"
      style={{ padding: "9px 14px", fontSize: 13.5 }}
    >
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round">
        <path d="M6 6l12 12M18 6 6 18" />
      </svg>
      {label}
    </button>
  );
}

/* A single "⋯" button that tucks the host-only utilities — join QR, full screen
   and end — into one popover, so the live header stays calm. */
function HostMenu({ pin, onExit }: { pin: string; onExit: () => void }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);

  const toggle = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 10, right: window.innerWidth - r.right });
    setOpen((o) => !o);
  };

  return (
    <>
      <button
        ref={btnRef}
        className="bk-btn bk-btn--ghost"
        onClick={toggle}
        title="Menu"
        aria-label="Menu"
        aria-expanded={open}
        style={{ padding: "9px 14px", fontSize: 13.5 }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" />
        </svg>
      </button>
      {open && pos && createPortal(
        <>
          {/* click-catcher so anywhere outside the menu closes it */}
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 9998 }} />
          <div
            className="bk-card"
            style={{
              position: "fixed",
              top: pos.top,
              right: pos.right,
              zIndex: 9999,
              padding: 18,
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              gap: 14,
              width: 226,
              animation: "bk-pop-in .15s ease both",
            }}
          >
            <div style={{ display: "flex", justifyContent: "center" }}>
              <JoinQR pin={pin} size={150} caption="SCAN TO JOIN" />
            </div>
            <div style={{ height: 1, background: "var(--line-soft)" }} />
            <FullscreenButton />
            <EndButton label="End quiz" onExit={() => { setOpen(false); onExit(); }} />
          </div>
        </>,
        document.body,
      )}
    </>
  );
}

/* Three-way exit prompt: end for everyone, leave but keep running, or stay. */
function HostExitModal({ onEnd, onLeave, onStay }: { onEnd: () => void; onLeave: () => void; onStay: () => void }) {
  return (
    <div
      className="bk"
      onClick={onStay}
      style={{ position: "fixed", inset: 0, zIndex: 100, display: "grid", placeItems: "center", background: "rgba(40,25,15,.5)", padding: 24 }}
    >
      <div
        className="bk-card"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 440, width: "100%", padding: 26, animation: "bk-pop-in .15s ease both" }}
      >
        <h2 style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-.02em" }}>Leave the live quiz?</h2>
        <p style={{ color: "var(--ink-soft)", fontWeight: 600, marginTop: 8, lineHeight: 1.5 }}>
          End it now for everyone, or step away and keep it running — you'll get a <b>Resume</b> button to come back to it.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 22 }}>
          <button className="bk-btn bk-btn--primary bk-btn--lg bk-btn--block" onClick={onEnd}>
            End the quiz for everyone
          </button>
          <button className="bk-btn bk-btn--ghost bk-btn--lg bk-btn--block" onClick={onLeave}>
            Leave &amp; keep it running
          </button>
          <button className="bk-btn bk-btn--block" onClick={onStay} style={{ background: "transparent", boxShadow: "none", color: "var(--ink-soft)" }}>
            Stay here
          </button>
        </div>
      </div>
    </div>
  );
}
